/**
 * Media Viewer Component
 * Beautiful fullscreen viewer for photos and videos with navigation
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Chip,
  Paper,
  Fade,
  Zoom,
  CircularProgress
} from '@mui/material';
import {
  Close,
  Download,
  NavigateNext,
  NavigateBefore,
  ZoomIn,
  ZoomOut,
  Info
} from '@mui/icons-material';

const isVideoItem = (item) =>
  item?.category === 'video' || item?.url?.match(/\.(mp4|mov|avi)$/i);

const MediaViewer = ({ open, onClose, media, allMedia = [], allowDownload = true }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [showInfo, setShowInfo] = useState(true);
  const [imageLoading, setImageLoading] = useState(false);

  // Parents often pass allMedia as an inline array (new identity every render),
  // so it must NOT be an effect dependency — that reset the index back to the
  // opened photo whenever the parent re-rendered, swallowing arrow clicks.
  const allMediaRef = useRef(allMedia);
  allMediaRef.current = allMedia;

  useEffect(() => {
    if (open && media && allMediaRef.current.length > 0) {
      const index = allMediaRef.current.findIndex(m => m.id === media.id);
      setCurrentIndex(index >= 0 ? index : 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, media?.id]);

  useEffect(() => {
    // Reset zoom when changing media
    setZoom(1);
  }, [currentIndex]);

  const currentMedia = allMedia[currentIndex] || media;

  // Full-size photos can take seconds on slow connections: show a spinner for
  // the incoming image and warm the browser cache for its neighbours so
  // Next/Previous feel instant.
  useEffect(() => {
    if (!open) return;
    const current = allMediaRef.current[currentIndex];
    if (current && !isVideoItem(current)) {
      setImageLoading(true);
    }
    [currentIndex - 1, currentIndex + 1].forEach((i) => {
      const neighbour = allMediaRef.current[i];
      if (neighbour?.url && !isVideoItem(neighbour)) {
        const img = new Image();
        img.src = neighbour.url;
      }
    });
  }, [open, currentIndex]);

  const handleNext = () => {
    setCurrentIndex(prev => Math.min(prev + 1, allMediaRef.current.length - 1));
  };

  const handlePrevious = () => {
    setCurrentIndex(prev => Math.max(prev - 1, 0));
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'ArrowRight') handleNext();
      else if (e.key === 'ArrowLeft') handlePrevious();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const handleDownload = async () => {
    if (!currentMedia?.url) return;

    const filename = currentMedia.fileName || currentMedia.name || 'project-file';
    try {
      const response = await fetch(currentMedia.url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      const link = document.createElement('a');
      link.href = currentMedia.url;
      link.download = filename;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!currentMedia) return null;

  const isVideo = isVideoItem(currentMedia);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.95)',
          backgroundImage: 'none'
        }
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative', overflow: 'hidden' }}>
        {/* Top Controls */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 2,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 100%)',
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
              {currentMedia.fileName || 'Media'}
            </Typography>
            {allMedia.length > 1 && (
              <Chip
                label={`${currentIndex + 1} / ${allMedia.length}`}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600
                }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isVideo && (
              <>
                <IconButton onClick={handleZoomOut} sx={{ color: 'white' }}>
                  <ZoomOut />
                </IconButton>
                <IconButton onClick={handleZoomIn} sx={{ color: 'white' }}>
                  <ZoomIn />
                </IconButton>
              </>
            )}
            <IconButton onClick={() => setShowInfo(!showInfo)} sx={{ color: 'white' }}>
              <Info />
            </IconButton>
            {allowDownload && (
              <IconButton onClick={handleDownload} sx={{ color: 'white' }}>
                <Download />
              </IconButton>
            )}
            <IconButton onClick={onClose} sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          </Box>
        </Box>

        {/* Media Content */}
        <Box
          sx={{
            width: '100%',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 8
          }}
        >
          {isVideo ? (
            <Box
              component="video"
              src={currentMedia.url}
              controls
              autoPlay
              sx={{
                maxWidth: '90%',
                maxHeight: '90%',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
              }}
            />
          ) : (
            <>
              {imageLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.5,
                    zIndex: 1
                  }}
                >
                  <CircularProgress sx={{ color: 'white' }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Loading photo {allMedia.length > 1 ? `${currentIndex + 1} of ${allMedia.length}` : ''}…
                  </Typography>
                </Box>
              )}
              <Box
                component="img"
                key={currentMedia.url}
                src={currentMedia.url}
                alt={currentMedia.description || 'Media'}
                onLoad={() => setImageLoading(false)}
                onError={() => setImageLoading(false)}
                sx={{
                  maxWidth: '90%',
                  maxHeight: '90%',
                  objectFit: 'contain',
                  transform: `scale(${zoom})`,
                  transition: 'transform 0.3s ease',
                  borderRadius: 2,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                  cursor: zoom > 1 ? 'move' : 'default',
                  opacity: imageLoading ? 0 : 1
                }}
              />
            </>
          )}
        </Box>

        {/* Navigation Arrows */}
        {allMedia.length > 1 && (
          <>
            <IconButton
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                width: 56,
                height: 56,
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.7)'
                },
                '&:disabled': {
                  opacity: 0.3
                }
              }}
            >
              <NavigateBefore sx={{ fontSize: 32 }} />
            </IconButton>

            <IconButton
              onClick={handleNext}
              disabled={currentIndex === allMedia.length - 1}
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                width: 56,
                height: 56,
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.7)'
                },
                '&:disabled': {
                  opacity: 0.3
                }
              }}
            >
              <NavigateNext sx={{ fontSize: 32 }} />
            </IconButton>
          </>
        )}

        {/* Info Panel */}
        {showInfo && (
          <Fade in={showInfo}>
            <Paper
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                p: 2,
                bgcolor: 'rgba(0,0,0,0.8)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                maxWidth: 600,
                width: '90%'
              }}
            >
              {currentMedia.description && (
                <Typography variant="body1" gutterBottom sx={{ fontWeight: 500 }}>
                  {currentMedia.description}
                </Typography>
              )}
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                {currentMedia.uploadedBy && (
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Uploaded by {currentMedia.uploadedBy}
                  </Typography>
                )}
                {currentMedia.uploadedAt && (
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {formatDate(currentMedia.uploadedAt)}
                  </Typography>
                )}
                {currentMedia.milestone && (
                  <Chip
                    label={currentMedia.milestone}
                    size="small"
                    sx={{
                      height: 20,
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontSize: '0.7rem'
                    }}
                  />
                )}
              </Box>
            </Paper>
          </Fade>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MediaViewer;
